import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { adminApi } from '../../../api/adminApi';

const TIPOS_ACCION = [
  { label: 'Todos', value: null },
  { label: 'Búsqueda por DNI', value: 'buscar-dni' },
  { label: 'Búsqueda por nombre', value: 'buscar-nombre' },
  { label: 'Escaneo QR', value: 'escanear-qr' },
  { label: 'Ver detalle', value: 'ver-detalle' },
];

const CON_RESULTADO = [
  { label: 'Todas', value: null },
  { label: 'Solo con resultado', value: 'true' },
  { label: 'Solo sin resultado', value: 'false' },
];

const ACCION_TAG = {
  'buscar-dni':     { label: 'DNI',        sev: 'info' },
  'buscar-nombre':  { label: 'Nombre',     sev: 'info' },
  'escanear-qr':    { label: 'QR',         sev: 'warning' },
  'ver-detalle':    { label: 'Ver detalle',sev: 'success' },
};

function toIsoDate(d) {
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function toHora(d) {
  if (!d) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatTimestamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'medium' });
}

export default function PanelConsultasAutoexcluidos() {
  const toast = useRef(null);
  const [filtros, setFiltros] = useState({
    casinoId: null,
    uid: null,
    fechaDesde: null,
    fechaHasta: null,
    horaDesde: null,
    horaHasta: null,
    dni: '',
    conResultado: null,
    tipoAccion: null,
  });
  const [casinos, setCasinos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [data, setData] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    cargarOpciones();
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function cargarOpciones() {
    try {
      const [casRes, usuRes] = await Promise.all([
        adminApi.getCasinos(),
        adminApi.getUsuarios(),
      ]);
      setCasinos([
        { label: 'Todos los casinos', value: null },
        ...casRes.data.map((c) => ({ label: c.nombre + (c.activo === false ? ' (inactivo)' : ''), value: c.id })),
      ]);
      setUsuarios([
        { label: 'Todos los usuarios', value: null },
        ...usuRes.data
          .filter((u) => ['agente', 'adminauto', 'admin'].includes(u.rol))
          .map((u) => ({ label: `${u.email} (${u.rol})`, value: u.id })),
      ]);
    } catch (e) {
      toast.current?.show({ severity: 'warn', summary: 'Advertencia', detail: 'No se pudieron cargar opciones de filtro' });
    }
  }

  function paramsFromFiltros(extra = {}) {
    const p = {};
    if (filtros.casinoId) p.casinoId = filtros.casinoId;
    if (filtros.uid) p.uid = filtros.uid;
    if (filtros.fechaDesde) p.fechaDesde = toIsoDate(filtros.fechaDesde);
    if (filtros.fechaHasta) p.fechaHasta = toIsoDate(filtros.fechaHasta);
    if (filtros.horaDesde) p.horaDesde = toHora(filtros.horaDesde);
    if (filtros.horaHasta) p.horaHasta = toHora(filtros.horaHasta);
    if (filtros.dni?.trim()) p.dni = filtros.dni.trim();
    if (filtros.conResultado) p.conResultado = filtros.conResultado;
    if (filtros.tipoAccion) p.tipoAccion = filtros.tipoAccion;
    return { ...p, ...extra };
  }

  function manejarError(e, fallback) {
    const status = e?.response?.status;
    const body = e?.response?.data;
    if (status === 503 && body?.message) {
      toast.current?.show({ severity: 'warn', summary: 'Índice en construcción', detail: body.message, life: 8000 });
    } else {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: body?.message || fallback });
    }
  }

  async function buscar() {
    setCargando(true);
    try {
      const res = await adminApi.getConsultasAutoexcluidos(paramsFromFiltros({ limit: 50 }));
      setData(res.data.entries);
      setHasMore(res.data.hasMore);
      setNextCursor(res.data.nextCursor);
    } catch (e) {
      manejarError(e, 'No se pudieron cargar las consultas');
    } finally {
      setCargando(false);
    }
  }

  async function cargarMas() {
    if (!nextCursor) return;
    setCargando(true);
    try {
      const res = await adminApi.getConsultasAutoexcluidos(paramsFromFiltros({ limit: 50, startAfter: nextCursor }));
      setData((prev) => [...prev, ...res.data.entries]);
      setHasMore(res.data.hasMore);
      setNextCursor(res.data.nextCursor);
    } catch (e) {
      manejarError(e, 'No se pudieron cargar más resultados');
    } finally {
      setCargando(false);
    }
  }

  function limpiarFiltros() {
    setFiltros({
      casinoId: null, uid: null, fechaDesde: null, fechaHasta: null,
      horaDesde: null, horaHasta: null, dni: '', conResultado: null, tipoAccion: null,
    });
  }

  // ─── Column templates ───────────────────────────────────────────────────
  const tsTpl = (row) => formatTimestamp(row.timestamp);
  const casinoTpl = (row) => row.casino?.nombre || <span style={{ color: '#bbb' }}>—</span>;
  const usuarioTpl = (row) => (
    <div style={{ fontSize: 13 }}>
      <div>{row.usuario?.email || '—'}</div>
      <small style={{ color: '#888' }}>{row.usuario?.rol || ''}</small>
    </div>
  );
  const accionTpl = (row) => {
    const a = ACCION_TAG[row.tipoAccion] || { label: row.tipoAccion, sev: 'secondary' };
    return <Tag value={a.label} severity={a.sev} />;
  };
  const resultadoTpl = (row) => {
    const r = row.resultado;
    if (!r) return '—';
    if (!r.encontrado) return <Tag value="No encontrado" severity="secondary" />;
    if (r.tipo === 'autoexcluido') return <Tag value="Autoexcluido" severity="danger" />;
    if (r.tipo === 'empleado') return <Tag value="Empleado" severity="success" />;
    if (r.tipo === 'busqueda-multiple') return <Tag value={`${r.cantidad} resultados`} severity="info" />;
    return <Tag value={r.tipo} />;
  };

  return (
    <div className="p-3">
      <Toast ref={toast} />

      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Consultas a Autoexcluidos</h2>
      </div>

      {/* ── Filtros ─────────────────────────────────────────────────────── */}
      <div style={{ background: '#f5f7fa', padding: 16, borderRadius: 8, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <Filtro label="Casino">
            <Dropdown value={filtros.casinoId} options={casinos} optionLabel="label" optionValue="value" onChange={(e) => setFiltros((p) => ({ ...p, casinoId: e.value }))} filter style={{ width: '100%' }} />
          </Filtro>
          <Filtro label="Usuario">
            <Dropdown value={filtros.uid} options={usuarios} optionLabel="label" optionValue="value" onChange={(e) => setFiltros((p) => ({ ...p, uid: e.value }))} filter style={{ width: '100%' }} />
          </Filtro>
          <Filtro label="Tipo de acción">
            <Dropdown value={filtros.tipoAccion} options={TIPOS_ACCION} optionLabel="label" optionValue="value" onChange={(e) => setFiltros((p) => ({ ...p, tipoAccion: e.value }))} style={{ width: '100%' }} />
          </Filtro>
          <Filtro label="Resultado">
            <Dropdown value={filtros.conResultado} options={CON_RESULTADO} optionLabel="label" optionValue="value" onChange={(e) => setFiltros((p) => ({ ...p, conResultado: e.value }))} style={{ width: '100%' }} />
          </Filtro>
          <Filtro label="DNI buscado">
            <InputText value={filtros.dni} onChange={(e) => setFiltros((p) => ({ ...p, dni: e.target.value }))} placeholder="Ej: 12345678" style={{ width: '100%' }} />
          </Filtro>
          <Filtro label="Fecha desde">
            <Calendar value={filtros.fechaDesde} onChange={(e) => setFiltros((p) => ({ ...p, fechaDesde: e.value }))} dateFormat="dd/mm/yy" style={{ width: '100%' }} />
          </Filtro>
          <Filtro label="Fecha hasta">
            <Calendar value={filtros.fechaHasta} onChange={(e) => setFiltros((p) => ({ ...p, fechaHasta: e.value }))} dateFormat="dd/mm/yy" style={{ width: '100%' }} />
          </Filtro>
          <Filtro label="Hora desde">
            <Calendar value={filtros.horaDesde} onChange={(e) => setFiltros((p) => ({ ...p, horaDesde: e.value }))} timeOnly hourFormat="24" style={{ width: '100%' }} />
          </Filtro>
          <Filtro label="Hora hasta">
            <Calendar value={filtros.horaHasta} onChange={(e) => setFiltros((p) => ({ ...p, horaHasta: e.value }))} timeOnly hourFormat="24" style={{ width: '100%' }} />
          </Filtro>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <Button label="Limpiar" icon="pi pi-times" text onClick={limpiarFiltros} disabled={cargando} />
          <Button label="Aplicar filtros" icon="pi pi-search" onClick={buscar} loading={cargando} />
        </div>
      </div>

      {/* ── Tabla de resultados ─────────────────────────────────────────── */}
      <DataTable
        value={data}
        loading={cargando}
        emptyMessage="No hay consultas que coincidan con los filtros"
        stripedRows
        size="small"
        scrollable
        scrollHeight="55vh"
      >
        <Column header="Fecha y hora" body={tsTpl} style={{ minWidth: 180 }} />
        <Column header="Casino" body={casinoTpl} style={{ minWidth: 160 }} />
        <Column header="Usuario" body={usuarioTpl} style={{ minWidth: 220 }} />
        <Column header="Acción" body={accionTpl} style={{ minWidth: 130 }} />
        <Column header="Búsqueda" field="valorBusqueda" style={{ minWidth: 160 }} />
        <Column header="Resultado" body={resultadoTpl} style={{ minWidth: 150 }} />
        <Column header="DNI encontrado" field="resultado.dniEncontrado" style={{ minWidth: 130 }} />
      </DataTable>

      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Button label="Cargar más" icon="pi pi-angle-down" onClick={cargarMas} loading={cargando} outlined />
        </div>
      )}

      <div style={{ marginTop: 8, color: '#888', fontSize: 13, textAlign: 'right' }}>
        {data.length} consulta(s) mostradas{hasMore ? ' (hay más)' : ''}
      </div>
    </div>
  );
}

function Filtro({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, color: '#666', fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}
